// Version: 2.8
// v2.8: Buttons use the shared button system (CV-49): 8px corners, standard variants and sizes, one selected look; no inline radius, padding or font size.
// v2.7: Optional Name when adding or editing a user (app.py v4.00); the list
//       shows the name with the email beside it.
// v2.6: Custom roles appear by name in the role dropdown (under a
//        "Custom roles" group) instead of a generic "Staff (custom role)"
//        entry plus a second dropdown.
import React, { useEffect, useState } from 'react';
import { authFetch, useAuth } from './auth';
import PasswordPolicy from './PasswordPolicy';
import './Users.css';

function ClientSelect({ clients, value, onChange, nameValue, onNameChange }) {
  return (
    <>
      <select value={value} onChange={e => onChange(e.target.value)} required>
        <option value="">— Select client —</option>
        {clients.map(c => <option key={c.slug} value={c.slug}>{c.display_name}</option>)}
        <option value="__new__">+ New client...</option>
      </select>
      {value === '__new__' && (
        <input type="text" value={nameValue} onChange={e => onNameChange(e.target.value)}
          placeholder="Client company name" required style={{marginTop:8}} />
      )}
    </>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-box" onClick={e => e.stopPropagation()}>
        <div className="modal-header">
          <div className="modal-title">{title}</div>
          <button className="modal-close" onClick={onClose}>✕</button>
        </div>
        <div className="modal-body">{children}</div>
      </div>
    </div>
  );
}

export default function Users() {
  const { user: currentUser } = useAuth();
  const [users, setUsers]     = useState([]);
  const [clients, setClients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError]     = useState('');
  const [success, setSuccess] = useState('');

  // New user form
  const [newEmail,      setNewEmail]      = useState('');
  const [newName,       setNewName]       = useState('');
  const [newRole,       setNewRole]       = useState('client');
  const [newClientSlug, setNewClientSlug] = useState('');
  const [newClientName, setNewClientName] = useState('');
  const [newCustomRole, setNewCustomRole] = useState('');
  const [roles, setRoles]                 = useState([]);
  const [creating,      setCreating]      = useState(false);
  const [createdPass,   setCreatedPass]   = useState('');

  // Edit user modal — stores the full user object so we can compare on save
  const [editTarget,     setEditTarget]     = useState(null);
  const [editRole,       setEditRole]       = useState('');
  const [editEmail,      setEditEmail]      = useState('');
  const [editName,       setEditName]       = useState('');
  const [editClientSlug, setEditClientSlug] = useState('');
  const [editClientName, setEditClientName] = useState('');
  const [editCustomRole, setEditCustomRole] = useState('');
  const [editMfaEnabled, setEditMfaEnabled] = useState(true); // true = MFA on
  const [editSaving,     setEditSaving]     = useState(false);

  // Password change modal
  const [pwTarget, setPwTarget] = useState(null);
  const [pwValue,  setPwValue]  = useState('');
  const [pwSaving, setPwSaving] = useState(false);

  const fetchUsers   = () => { authFetch('/api/users').then(r => r.json()).then(d => { setUsers(d); setLoading(false); }).catch(() => setLoading(false)); };
  const fetchClients = () => { authFetch('/api/clients').then(r => r.json()).then(d => setClients(d)).catch(() => {}); };
  const fetchRoles   = () => { authFetch('/api/admin/roles').then(r => r.json()).then(d => setRoles(d.roles || [])).catch(() => {}); };

  useEffect(() => { fetchUsers(); fetchClients(); fetchRoles(); }, []);

  const flash = (msg, isError = false) => {
    if (isError) { setError(msg); setSuccess(''); } else { setSuccess(msg); setError(''); }
    setTimeout(() => { setError(''); setSuccess(''); }, 6000);
  };

  const handleCreate = async (e) => {
    e.preventDefault(); setCreating(true); setCreatedPass('');
    const slug = newClientSlug === '__new__' ? newClientName.trim().replace(/\s+/g, '_') : newClientSlug;
    try {
      const res = await authFetch('/api/users', {
        method: 'POST',
        body: JSON.stringify({ email: newEmail.trim(), name: newName.trim(), role: newRole, client_slug: slug, custom_role: newRole === 'staff' ? newCustomRole : '' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed to create user');
      flash(data.email_sent ? `User created — welcome email sent to ${newEmail}` : `User created — email failed, temp password: ${data.temp_password}`);
      setCreatedPass(data.temp_password);
      setNewEmail(''); setNewName(''); setNewRole('client'); setNewClientSlug(''); setNewClientName(''); setNewCustomRole('');
      fetchUsers();
    } catch (err) { flash(err.message, true); }
    finally { setCreating(false); }
  };

  const handleEditOpen = (u) => {
    setEditTarget(u);
    setEditRole(u.role);
    setEditEmail(u.email);
    setEditName(u.name || '');
    setEditClientSlug(u.client_slug || '');
    setEditClientName('');
    setEditCustomRole(u.custom_role || '');
    setEditMfaEnabled(!u.mfa_disabled); // convert stored flag to UI sense
  };

  const handleEditSave = async (e) => {
    e.preventDefault(); setEditSaving(true);
    const slug = editClientSlug === '__new__' ? editClientName.trim().replace(/\s+/g, '_') : editClientSlug;
    try {
      // 1. Save role / email / client
      const res = await authFetch(`/api/users/${encodeURIComponent(editTarget.username)}`, {
        method: 'PUT',
        body: JSON.stringify({ role: editRole, email: editEmail, name: editName.trim(), client_slug: editRole === 'client' ? slug : '', custom_role: editRole === 'staff' ? editCustomRole : '' })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Update failed');

      // 2. If MFA state changed, fire a second call (only for non-self)
      const isSelf = editTarget.username === currentUser?.username;
      const mfaChanged = editMfaEnabled === editTarget.mfa_disabled; // enabled !== !disabled means changed
      if (!isSelf && mfaChanged) {
        const mfaRes = await authFetch(`/api/users/${encodeURIComponent(editTarget.username)}/mfa`, {
          method: 'PUT',
          body: JSON.stringify({ mfa_disabled: !editMfaEnabled })
        });
        if (!mfaRes.ok) {
          const mfaData = await mfaRes.json();
          throw new Error(mfaData.error || 'MFA update failed');
        }
      }

      flash('User updated');
      setEditTarget(null);
      fetchUsers();
    } catch (err) { flash(err.message, true); }
    finally { setEditSaving(false); }
  };

  const handleBlock = async (username, isBlocked) => {
    try {
      const res = await authFetch(`/api/users/${encodeURIComponent(username)}/block`, { method: 'PUT' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Failed');
      flash(isBlocked ? 'User unblocked' : 'User blocked');
      fetchUsers();
    } catch (err) { flash(err.message, true); }
  };

  const handleDelete = async (username) => {
    if (!window.confirm(`Permanently delete "${username}"? This cannot be undone.`)) return;
    try {
      const res = await authFetch(`/api/users/${encodeURIComponent(username)}`, { method: 'DELETE' });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Delete failed');
      flash('User deleted');
      fetchUsers();
    } catch (err) { flash(err.message, true); }
  };

  const handlePwChange = async (e) => {
    e.preventDefault(); setPwSaving(true);
    try {
      const res = await authFetch(`/api/users/${encodeURIComponent(pwTarget.username)}/password`, {
        method: 'PUT', body: JSON.stringify({ password: pwValue })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Password change failed');
      flash('Password updated');
      setPwTarget(null); setPwValue('');
    } catch (err) { flash(err.message, true); }
    finally { setPwSaving(false); }
  };

  const isSelf = (username) => username === currentUser?.username;

  return (
    <div>
      <div className="page-header">
        <div className="page-title">Users</div>
        <div className="page-subtitle">Manage portal access</div>
      </div>

      {error   && <div className="error-msg"   style={{marginBottom:20}}>{error}</div>}
      {success && <div className="success-msg" style={{marginBottom:20}}>{success}</div>}

      <div className="users-grid">
        <div>
          <div className="card" style={{marginBottom:24}}>
            <div className="users-table-title">Current Users</div>
            {loading ? <div style={{padding:24,textAlign:'center'}}><span className="spinner" /></div> : (
              <div className="users-list">
                {users.map(u => (
                  <div key={u.username} className="user-row" style={{opacity: u.blocked ? 0.6 : 1}}>
                    <div className="user-row-main">
                      <span className="user-chip-avatar" style={{width:32,height:32,fontSize:13,flexShrink:0}}>{(u.name || u.username)[0].toUpperCase()}</span>
                      <div className="user-row-info">
                        <div style={{display:'flex',alignItems:'center',gap:8,flexWrap:'wrap'}}>
                          <span style={{fontSize:13,fontWeight:600,color:'var(--text)'}}>{u.name || u.username}</span>
                          {u.name && <span style={{fontSize:12,color:'var(--text-muted)'}}>{u.username}</span>}
                          {isSelf(u.username) && <span className="tag tag-blue" style={{fontSize:10,padding:'2px 6px'}}>you</span>}
                          <span className={`tag ${u.role === 'admin' ? 'tag-amber' : u.role === 'manager' ? 'tag-blue' : u.role === 'staff' ? 'tag-green' : 'tag-muted'}`}>{u.role === 'staff' ? (roles.find(r => r.id === u.custom_role)?.name || 'staff') : u.role}</span>
                          {u.blocked ? <span className="tag tag-red">Blocked</span>
                            : u.password_change_required ? <span className="tag tag-amber">PW required</span>
                            : <span className="tag tag-green">Active</span>}
                        </div>
                        {u.client_slug && (
                          <div style={{fontSize:12,color:'var(--text-muted)',marginTop:3}}>
                            {clients.find(x => x.slug === u.client_slug)?.display_name || u.client_slug.replace(/_/g,' ')}
                          </div>
                        )}
                      </div>
                      <div className="user-row-actions">
                        <button className="btn btn-secondary btn-sm" onClick={() => handleEditOpen(u)}>Edit</button>
                        <button className="btn btn-secondary btn-sm" onClick={() => { setPwTarget(u); setPwValue(''); }}>Password</button>
                        {/* MFA status — shown for all users, clickable only for others */}
                        {isSelf(u.username)
                          ? <span style={{fontSize:11,padding:'4px 10px',color: u.mfa_disabled ? 'var(--amber)' : 'var(--text-muted)',opacity:0.6}}>{u.mfa_disabled ? 'MFA off' : 'MFA on'}</span>
                          : <button className="btn btn-secondary btn-sm" style={{color: u.mfa_disabled ? 'var(--amber-ink)' : undefined}} onClick={() => handleEditOpen(u)} title={u.mfa_disabled ? 'MFA disabled — click Edit to change' : 'MFA enabled — click Edit to change'}>
                              {u.mfa_disabled ? 'MFA off' : 'MFA on'}
                            </button>
                        }
                        {/* Block and Delete — not available on own account */}
                        {!isSelf(u.username) && <>
                          <button className="btn btn-secondary btn-sm" style={{color: u.blocked ? 'var(--green-ink)' : 'var(--amber-ink)'}} onClick={() => handleBlock(u.username, u.blocked)}>
                            {u.blocked ? 'Unblock' : 'Block'}
                          </button>
                          <button className="btn btn-danger btn-sm" onClick={() => handleDelete(u.username)}>Delete</button>
                        </>}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>

        <div>
          {/* Add user */}
          <div className="card" style={{marginBottom:24}}>
            <div className="users-table-title">Add User</div>
            <form onSubmit={handleCreate} style={{display:'flex',flexDirection:'column',gap:16,marginTop:16}}>
              <div>
                <label>Email (used as username)</label>
                <input type="email" value={newEmail} onChange={e => setNewEmail(e.target.value)} placeholder="user@company.com" required />
                <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>Welcome email with temp password will be sent here</div>
              </div>
              <div>
                <label>Name (optional)</label>
                <input type="text" value={newName} onChange={e => setNewName(e.target.value)} placeholder="First and last name" maxLength={100} />
              </div>
              <div>
                <label>Role</label>
                <select value={newRole === 'staff' ? `staff:${newCustomRole}` : newRole}
                  onChange={e => { const v = e.target.value;
                    if (v.startsWith('staff:')) { setNewRole('staff'); setNewCustomRole(v.slice(6)); setNewClientSlug(''); }
                    else { setNewRole(v); setNewCustomRole(''); if (v !== 'client') setNewClientSlug(''); } }}>
                  <option value="client">Client</option>
                  <option value="manager">Manager</option>
                  <option value="admin">Admin</option>
                  {roles.length > 0 && (
                    <optgroup label="Custom roles">
                      {roles.map(r => <option key={r.id} value={`staff:${r.id}`}>{r.name}</option>)}
                    </optgroup>
                  )}
                </select>
                {newRole === 'staff' && <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>Manage roles and their permissions on the Roles page</div>}
              </div>
              {newRole === 'client' && (
                <div>
                  <label>Client</label>
                  <ClientSelect clients={clients} value={newClientSlug} onChange={setNewClientSlug} nameValue={newClientName} onNameChange={setNewClientName} />
                  <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>This user will only see this client's reports</div>
                </div>
              )}
              <button className="btn btn-primary" type="submit" disabled={creating}>
                {creating ? <span className="spinner" /> : 'Create User and Send Email'}
              </button>
            </form>
            {createdPass && (
              <div className="success-msg" style={{marginTop:16}}>
                <div style={{fontWeight:600,marginBottom:4}}>Temp password (in case email failed):</div>
                <div style={{fontFamily:'var(--font-mono)',fontSize:13}}>{createdPass}</div>
              </div>
            )}
            <div className="users-hint">A temporary password is generated automatically and emailed to the user. They will be prompted to change it on first login.</div>
          </div>

          <PasswordPolicy />
        </div>
      </div>

      {/* Edit user modal */}
      {editTarget && (
        <Modal title={`Edit User — ${editTarget.username}`} onClose={() => setEditTarget(null)}>
          <form onSubmit={handleEditSave} style={{display:'flex',flexDirection:'column',gap:14}}>
            <div>
              <label>Email</label>
              <input type="email" value={editEmail} onChange={e => setEditEmail(e.target.value)} required />
            </div>
            <div>
              <label>Name (optional)</label>
              <input type="text" value={editName} onChange={e => setEditName(e.target.value)} placeholder="First and last name" maxLength={100} />
            </div>
            <div>
              <label>Role</label>
              <select value={editRole === 'staff' ? `staff:${editCustomRole}` : editRole}
                onChange={e => { const v = e.target.value;
                  if (v.startsWith('staff:')) { setEditRole('staff'); setEditCustomRole(v.slice(6)); setEditClientSlug(''); }
                  else { setEditRole(v); setEditCustomRole(''); if (v !== 'client') setEditClientSlug(''); } }}>
                <option value="client">Client</option>
                <option value="manager">Manager</option>
                <option value="admin">Admin</option>
                {roles.length > 0 && (
                  <optgroup label="Custom roles">
                    {roles.map(r => <option key={r.id} value={`staff:${r.id}`}>{r.name}</option>)}
                  </optgroup>
                )}
              </select>
              {editRole === 'staff' && <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>Manage roles and their permissions on the Roles page</div>}
            </div>
            {editRole === 'client' && (
              <div>
                <label>Client</label>
                <ClientSelect clients={clients} value={editClientSlug} onChange={setEditClientSlug} nameValue={editClientName} onNameChange={setEditClientName} />
              </div>
            )}
            {/* MFA — disabled checkbox for own account, editable for others */}
            {isSelf(editTarget.username) && (
              <div>
                <label className="users-mfa-label users-mfa-label--disabled">
                  <input
                    type="checkbox"
                    checked={editMfaEnabled}
                    disabled
                    style={{width:'auto',margin:0,flexShrink:0}}
                  />
                  <span>MFA enabled</span>
                </label>
                <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>
                  MFA settings for your own account can only be changed by another admin.
                </div>
              </div>
            )}
            {!isSelf(editTarget.username) && (
              <div>
                <label className="users-mfa-label">
                  <input
                    type="checkbox"
                    checked={editMfaEnabled}
                    onChange={e => setEditMfaEnabled(e.target.checked)}
                    style={{width:'auto',margin:0,flexShrink:0}}
                  />
                  <span>MFA enabled</span>
                </label>
                <div style={{fontSize:12,color:'var(--text-muted)',marginTop:4}}>Uncheck to disable two-factor authentication for this user</div>
              </div>
            )}
            <div style={{display:'flex',gap:8,paddingTop:4}}>
              <button className="btn btn-primary" type="submit" disabled={editSaving}>{editSaving ? <span className="spinner" /> : 'Save Changes'}</button>
              <button className="btn btn-secondary" type="button" onClick={() => setEditTarget(null)}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}

      {/* Password change modal */}
      {pwTarget && (
        <Modal title={`Change Password — ${pwTarget.username}`} onClose={() => { setPwTarget(null); setPwValue(''); }}>
          <form onSubmit={handlePwChange} style={{display:'flex',flexDirection:'column',gap:14}}>
            <div>
              <label>New Password</label>
              <input type="password" value={pwValue} onChange={e => setPwValue(e.target.value)} placeholder="Min 12 characters" required />
            </div>
            <div style={{display:'flex',gap:8,paddingTop:4}}>
              <button className="btn btn-primary" type="submit" disabled={pwSaving}>{pwSaving ? <span className="spinner" /> : 'Save Password'}</button>
              <button className="btn btn-secondary" type="button" onClick={() => { setPwTarget(null); setPwValue(''); }}>Cancel</button>
            </div>
          </form>
        </Modal>
      )}
    </div>
  );
}